import { Action } from "redux";
import { Table } from "../../api/tables";
import { Reservation } from "../../api/reservations";
import { OccupiedDay } from "../../api/occupied-days";
import { Area } from "../../api/areas";
import { Capabilities } from "../../integration/capabilities";
import { Inquiry } from "../../api/inquiries";
import { AuthInfo } from "../../api/token-storage";
import { MessageSignature } from "../../api/settings";
import { Meal } from '../../api/meals';
import { Payment } from '../../api/payments';

export enum ActionType {
	AppLoginStatusUpdated = "@@app/LOGIN_STATUS_UPDATED",
	AppAuthInfoUpdated = "@@app/AUTH_INFO_UPDATED",
	AppDateUpdated = "@@app/DATE_UPDATED",
	AppSnackbarMessageUpdated = "@@app/SNACKBAR_MESSAGE_UPDATED",
	AppTablesUpdated = "@@app/TABLES_UPDATED",
	AppAreasUpdated = "@@app/AREAS_UPDATED",
	AppReservationsUpdated = "@@app/RESERVATIONS_UPDATED",
	AppSingleReservationUpdated = "@@app/SINGLE_RESERVATION_UPDATED",
	AppReservationInquiriesUpdated = "@@app/RESERVATION_INQUIRIES_UPDATED",
	AppSingleReservationInquiryUpdated = "@@app/SINGLE_RESERVATION_INQUIRY_UPDATED",
	AppReservationInquiryRemoved = "@@app/RESERVATION_INQUIRY_REMOVED",
	AppReservationAdded = "@@app/RESERVATION_ADDED",
	AppReservationRemoved = "@@app/RESERVATION_REMOVED",
	AppInquiryAdded = "@@app/INQUIRY_ADDED",
	AppInquiriesUpdated = "@@app/INQUIRIES_UPDATED",
	AppArchivedInquiriesUpdated = "@@app/ARCHIVED_INQUIRIES_UPDATED",
	AppArchivedInquiriesViewToggled = "@@app/ARCHIVED_INQUIRIES_VIEW_TOGGLED",
	AppSingleInquiryUpdated = "@@app/SINGLE_INQUIRY_UPDATED",
	AppInquiryRemoved = "@@app/INQUIRY_REMOVED",
	AppArchivedInquiryRemoved = "@@app/ARCHIVED_INQUIRY_REMOVED",
	AppOccupiedDaysUpdated = "@@app/OCCUPIED_DAYS_UDPATED",
	AppSingleOccupiedDayUpdated = "@@app/SINGLE_OCCUPIED_DAY_UDPATED",
	AppPaymentsUpdated = "@@app/PAYMENTS_UPDATED",
	AppSinglePaymentUpdated = "app/SINGLE_PAYMENT_UPDATED",
	AppLayoutChanged = "@@app/LAYOUT_CHANGED",
	AppPaniced = "@@app/PANICED",
	AppAddReservationDialogOpened = "@@app/ADD_RESERVATION_DIALOG_OPENED",
	AppAddReservationDialogClosed = "@@app/ADD_RESERVATION_DIALOG_CLOSED",
	AppEditReservationDialogOpened = "@@app/EDIT_RESERVATION_DIALOG_OPENED",
	AppAddPaymentDialogOpened = "@@app/ADD_PAYMENT_DIALOG_OPENED",
	AppAddPaymentDialogClosed = "@@app/ADD_PAYMENT_DIALOG_CLOSED",
	AppEditAvailabilitiesDialogOpened = "@@app/EDIT_AVAILABILITIES_DIALOG_OPENED",
	AppEditAvailabilitiesDialogClosed = "@@app/EDIT_AVAILABILITIES_DIALOG_CLOSED",
	AppAvailabilityDatepickerUpdated = "@@app/AVAILABILITY_DATEPICKER_UPDATED",
	AppEditInquiryDialogOpened = "@@app/EDIT_INQUIRY_DIALOG_OPENED",
	AppEditInquiryDialogClosed = "@@app/EDIT_INQUIRY_DIALOG_CLOSED",
	AppTableLinkModeActivated = "@@app/TABLE_LINK_MODE_ACTIVATED",
	AppTableLinkModeDeactivated = "@@app/TABLE_LINK_MODE_DEACTIVATED",
	AppTableLinkDeletionModeActivated = "@@app/TABLE_LINK_DELETION_MODE_ACTIVATED",
	AppTableLinkTargetGroupSet = "@@app/TABLE_LINK_TARGET_GROUP_SET",
	AppTableLinkTableAdded = "@@app/TABLE_LINK_TABLE_ADDED",
	AppTableLinkTableRemoved = "@@app/TABLE_LINK_TABLE_REMOVED",
	AppTableLinkGroupsUpdated = "@@app/TABLE_LINK_GROUPS_UPDATED",
	AppCapabilitiesUpdated = "@@app/CAPABILITIES_UPDATED",
	SettingsSignaturesUpdated = "@@settings/SIGNATURES_UPDATED",
	SettingsSignatureAdded = "@@settings/SIGNATURE_ADDED",
	SettingsSignatureRemoved = "@@settings/SIGNATURE_REMOVED",
	SettingsSingleSignatureUpdated = "@@settings/SINGLE_SIGNATURE_UPDATED",
	SettingsMealsUpdated = "@@settings/MEALS_UPDATED",
	SettingsMealAdded = "@@settings/MEAL_ADDED",
	SettingsMealRemoved = "@@settings/MEAL_REMOVED",
	SettingsSingleMealUpdated = "@@settings/SINGLE_MEAL_UPDATED",
	OverviewAllAreasToggleChanged = "@@overview/ALL_AREAS_TOGGLE_CHANGED",
	OverviewActiveAreaChanged = "@@overview/ACTIVE_AREA_CHANGED",
	OverviewActiveTimeChanged = "@@overview/ACTIVE_TIME_CHANGED",
	OverviewActiveReservationChanged = "@@overview/ACTIVE_RESERVATION_CHANGED",
	OverviewInfoBarToggled = "@@overview/INFO_BAR_TOGGLED",
	OverviewAdditionalInfoShown = "@@overview/ADDITIONAL_INFO_SHOWN",
	OverviewAdditionalInfoHidden = "@@overview/ADDITIONAL_INFO_HIDDEN",
	PlannerListMovingReservationSet = "@@planner-list/MOVING_RESERVATION_SET",
	InquiryListDateRangeChanged = "@@inquiry-list/DATE_RANGE_CHANGED",
	InquiryListHighlightedInquiryChanged = "@@inquiry-list/HIGHLIGHTED_INQUIRY_CHANGED",
	UIDraggingStarted = "@@ui/DRAGGING_STARTED",
	UIDraggingEnded = "@@ui/DRAGGING_ENDED",
	UIDrawerOpened = "@@ui/DRAWER_OPENED",
	UIDrawerClosed = "@@ui/DRAWER_CLOSED",
	UIDrawerToggled = "@@ui/DRAWER_TOGGLED"
}

/**
 * App
 */

export interface LoginStatusUpdatedAction extends Action {
	type: ActionType.AppLoginStatusUpdated;
	payload: {
		status: boolean;
	};
}

export interface AuthInfoUpdatedAction extends Action {
	type: ActionType.AppAuthInfoUpdated;
	payload: {
		authInfo: AuthInfo;
	};
}

export interface DateUpdatedAction extends Action {
	type: ActionType.AppDateUpdated;
	payload: {
		date: Date;
	};
}

export interface TablesUpdatedAction extends Action {
	type: ActionType.AppTablesUpdated;
	payload: {
		tables: Table[];
	};
}

export interface AreasUpdatedAction extends Action {
	type: ActionType.AppAreasUpdated;
	payload: {
		areas: Area[];
	};
}

export interface ReservationsUpdatedAction extends Action {
	type: ActionType.AppReservationsUpdated;
	payload: {
		reservations: Reservation[];
	};
}

export interface SingleReservationUpdatedAction extends Action {
	type: ActionType.AppSingleReservationUpdated;
	payload: {
		id: string;
		reservation: Reservation;
	};
}

export interface ReservationInquiriesUpdatedAction extends Action {
	type: ActionType.AppReservationInquiriesUpdated;
	payload: {
		inquiries: Reservation[];
	};
}

export interface SingleReservationInquiryUpdatedAction extends Action {
	type: ActionType.AppSingleReservationInquiryUpdated;
	payload: {
		id: string;
		inquiry: Reservation;
	};
}

export interface InquiryRemovedAction extends Action {
	type: ActionType.AppInquiryRemoved;
	payload: {
		id: string;
	};
}

export interface ArchivedInquiryRemovedAction extends Action {
	type: ActionType.AppArchivedInquiryRemoved;
	payload: {
		id: string;
	};
}

export interface ReservationAddedAction extends Action {
	type: ActionType.AppReservationAdded;
	payload: {
		reservation: Reservation;
	};
}

export interface ReservationRemovedAction extends Action {
	type: ActionType.AppReservationRemoved;
	payload: {
		id: string;
	};
}

export interface InquiryAddedAction extends Action {
	type: ActionType.AppInquiryAdded;
	payload: {
		inquiry: Inquiry;
	};
}

export interface InquiriesUpdatedAction extends Action {
	type: ActionType.AppInquiriesUpdated;
	payload: {
		inquiries: Inquiry[];
	};
}

export interface ArchivedInquiriesUpdatedAction extends Action {
	type: ActionType.AppArchivedInquiriesUpdated;
	payload: {
		archivedInquiries: Inquiry[];
	};
}

export interface ArchivedInquiriesViewToggledAction extends Action {
	type: ActionType.AppArchivedInquiriesViewToggled;
}

export interface SingleInquiryUpdatedAction extends Action {
	type: ActionType.AppSingleInquiryUpdated;
	payload: {
		id: string;
		inquiry: Inquiry;
	};
}

export interface OccupiedDaysUpdatedAction extends Action {
	type: ActionType.AppOccupiedDaysUpdated;
	payload: {
		days: OccupiedDay[];
	};
}

export interface SingleOccupiedDayUpdatedAction extends Action {
	type: ActionType.AppSingleOccupiedDayUpdated;
	payload: {
		id: string;
		updateData: OccupiedDay;
	};
}

export interface PaymentsUpdatedAction extends Action {
	type: ActionType.AppPaymentsUpdated;
	payload: {
		payments: Payment[];
		page: number;
		count: number;
	};
}

export interface SinglePaymentUpdatedAction extends Action {
	type: ActionType.AppSinglePaymentUpdated;
	payload: {
		id: string;
		updateData: Payment;
	};
}

export interface LayoutChangedAction extends Action {
	type: ActionType.AppLayoutChanged;
	payload: {
		layout: "desktop" | "tablet" | "phone";
	};
}

export interface PanicAction extends Action {
	type: ActionType.AppPaniced;
}

export interface AddReservationDialogOpenedAction extends Action {
	type: ActionType.AppAddReservationDialogOpened;
}

export interface AddReservationDialogClosedAction extends Action {
	type: ActionType.AppAddReservationDialogClosed;
}

export interface EditReservationDialogOpenedAction extends Action {
	type: ActionType.AppEditReservationDialogOpened;
}

export interface EditAvailabilitiesDialogOpenedAction extends Action {
	type: ActionType.AppEditAvailabilitiesDialogOpened;
}

export interface EditAvailabilitiesDialogClosedAction extends Action {
	type: ActionType.AppEditAvailabilitiesDialogClosed;
}

export interface AvailabilityDatepickerUpdatedAction extends Action {
	type: ActionType.AppAvailabilityDatepickerUpdated;
}

export interface AddPaymentDialogOpenedAction extends Action {
	type: ActionType.AppAddPaymentDialogOpened;
	payload: {
		inquiryId?: string;
		email?: string;
		name?: string;
		amount?: string;
		validHours: number;
		message?: string;
		paymentId?: string;
		// Card requested for an existing reservation (occupancy plan)
		reservationId?: string;
	}
}

export interface AddPaymentDialogClosedAction extends Action {
	type: ActionType.AppAddPaymentDialogClosed;
}

export interface EditInquiryDialogOpenedAction extends Action {
	type: ActionType.AppEditInquiryDialogOpened;
	payload: {
		inquiry: Inquiry;
	}
}

export interface EditInquiryDialogClosedAction extends Action {
	type: ActionType.AppEditInquiryDialogClosed;
}

export interface TableLinkModeActivatedAction extends Action {
	type: ActionType.AppTableLinkModeActivated;
}

export interface TableLinkModeDeactivatedAction extends Action {
	type: ActionType.AppTableLinkModeDeactivated;
}

export interface TableLinkDeletionModeActivatedAction extends Action {
	type: ActionType.AppTableLinkDeletionModeActivated;
}

export interface TableLinkTargetGroupSetAction extends Action {
	type: ActionType.AppTableLinkTargetGroupSet;
	payload: {
		index: number;
	};
}

export interface TableLinkTableAddedAction extends Action {
	type: ActionType.AppTableLinkTableAdded;
	payload: {
		id: string;
	};
}

export interface TableLinkTableRemovedAction extends Action {
	type: ActionType.AppTableLinkTableRemoved;
	payload: {
		id: string;
	};
}

export interface TableLinkGroupsUpdatedAction extends Action {
	type: ActionType.AppTableLinkGroupsUpdated;
	payload: {
		groups: string[][];
	};
}

export interface CapabilitiesUpdatedAction extends Action {
	type: ActionType.AppCapabilitiesUpdated;
	payload: {
		capabilities: Capabilities;
	};
}

export type AppActions =
	| LoginStatusUpdatedAction
	| AuthInfoUpdatedAction
	| DateUpdatedAction
	| TablesUpdatedAction
	| AreasUpdatedAction
	| ReservationsUpdatedAction
	| SingleReservationUpdatedAction
	| ReservationAddedAction
	| ReservationRemovedAction
	| InquiryAddedAction
	| InquiriesUpdatedAction
	| ArchivedInquiriesUpdatedAction
	| ArchivedInquiriesViewToggledAction
	| SingleInquiryUpdatedAction
	| InquiryRemovedAction
	| ArchivedInquiryRemovedAction
	| OccupiedDaysUpdatedAction
	| SingleOccupiedDayUpdatedAction
	| PaymentsUpdatedAction
	| SinglePaymentUpdatedAction
	| ReservationInquiriesUpdatedAction
	| SingleReservationInquiryUpdatedAction
	| LayoutChangedAction
	| PanicAction
	| AddReservationDialogOpenedAction
	| AddReservationDialogClosedAction
	| EditReservationDialogOpenedAction
	| EditAvailabilitiesDialogOpenedAction
	| EditAvailabilitiesDialogClosedAction
	| AvailabilityDatepickerUpdatedAction
	| AddPaymentDialogOpenedAction
	| AddPaymentDialogClosedAction
	| EditInquiryDialogOpenedAction
	| EditInquiryDialogClosedAction
	| TableLinkModeActivatedAction
	| TableLinkModeDeactivatedAction
	| TableLinkDeletionModeActivatedAction
	| TableLinkTargetGroupSetAction
	| TableLinkTableAddedAction
	| TableLinkTableRemovedAction
	| TableLinkGroupsUpdatedAction
	| CapabilitiesUpdatedAction;

/**
 * Settings
 */

export interface SettingsSignaturesUpdatedAction extends Action {
	type: ActionType.SettingsSignaturesUpdated;
	payload: {
		signatures: MessageSignature[];
	};
}

export interface SettingsSignatureAddedAction extends Action {
	type: ActionType.SettingsSignatureAdded;
	payload: {
		signature: MessageSignature;
	};
}

export interface SettingsSignatureRemovedAction extends Action {
	type: ActionType.SettingsSignatureRemoved;
	payload: {
		id: string;
	};
}

export interface SettingsSingleSignatureUpdatedAction extends Action {
	type: ActionType.SettingsSingleSignatureUpdated;
	payload: {
		id: string;
		signature: MessageSignature;
	};
}

export interface SettingsMealsUpdatedAction extends Action {
	type: ActionType.SettingsMealsUpdated;
	payload: {
		meals: Meal[];
	};
}

export interface SettingsMealAddedAction extends Action {
	type: ActionType.SettingsMealAdded;
	payload: {
		meal: Meal;
	};
}

export interface SettingsMealRemovedAction extends Action {
	type: ActionType.SettingsMealRemoved;
	payload: {
		id: string;
	};
}

export interface SettingsSingleMealUpdatedAction extends Action {
	type: ActionType.SettingsSingleMealUpdated;
	payload: {
		id: string;
		meal: Meal;
	};
}

export type SettingsActions =
	| SettingsSignatureAddedAction
	| SettingsSignaturesUpdatedAction
	| SettingsSignatureRemovedAction
	| SettingsSingleSignatureUpdatedAction
	| SettingsMealAddedAction
	| SettingsMealsUpdatedAction
	| SettingsMealRemovedAction
	| SettingsSingleMealUpdatedAction;

/**
 * Snackbar
 */

export type SnackbarVariant = "info" | "warning" | "error" | "success";

export interface SnackbarClickPayload<T> {
	type: string;
	data: T;
}

export interface SnackbarMessageUpdatedAction extends Action {
	type: ActionType.AppSnackbarMessageUpdated;
	payload: {
		message: string;
		variant?: SnackbarVariant;
	};
}

export type SnackbarActions = SnackbarMessageUpdatedAction;

/**
 * Overview
 */

export interface AllAreasToggleChangedAction extends Action {
	type: ActionType.OverviewAllAreasToggleChanged;
	payload: {
		status: boolean;
	};
}

export interface ActiveAreaChangedAction extends Action {
	type: ActionType.OverviewActiveAreaChanged;
	payload: {
		id: string;
	};
}

export interface ActiveTimeChangedAction extends Action {
	type: ActionType.OverviewActiveTimeChanged;
	payload: {
		id: string;
	};
}

export interface ActiveReservationChangedAction extends Action {
	type: ActionType.OverviewActiveReservationChanged;
	payload: {
		id: string;
	};
}

export interface InfoBarToggledAction extends Action {
	type: ActionType.OverviewInfoBarToggled;
}

export interface AdditionalInfoShownAction extends Action {
	type: ActionType.OverviewAdditionalInfoShown;
}

export interface AdditionalInfoHiddenAction extends Action {
	type: ActionType.OverviewAdditionalInfoHidden;
}

export type OverviewActions =
	| AllAreasToggleChangedAction
	| ActiveAreaChangedAction
	| ActiveTimeChangedAction
	| ActiveReservationChangedAction
	| InfoBarToggledAction
	| AdditionalInfoShownAction
	| AdditionalInfoHiddenAction;

/**
 * Inquiry List
 */

export interface PlannerListMovingReservationSetAction extends Action {
	type: ActionType.PlannerListMovingReservationSet;
	payload: {
		id: string;
	};
}

export type PlannerListActions = PlannerListMovingReservationSetAction;

/**
 * Inquiry List
 */

export interface InquiryListDateRangeChangedAction extends Action {
	type: ActionType.InquiryListDateRangeChanged;
	payload: {
		startDate: Date;
		endDate: Date;
	};
}

export interface InquiryListHighlightedInquiryChangedAction extends Action {
	type: ActionType.InquiryListHighlightedInquiryChanged;
	payload: {
		id: string;
	};
}

export type InquiryListActions =
	| InquiryListDateRangeChangedAction
	| InquiryListHighlightedInquiryChangedAction;

/**
 * UI
 */

export interface DraggingStartedAction extends Action {
	type: ActionType.UIDraggingStarted;
}

export interface DraggingEndedAction extends Action {
	type: ActionType.UIDraggingEnded;
}

export interface DrawerOpenedAction extends Action {
	type: ActionType.UIDrawerOpened;
}

export interface DrawerClosedAction extends Action {
	type: ActionType.UIDrawerClosed;
}

export interface DrawerToggledAction extends Action {
	type: ActionType.UIDrawerToggled;
}

export type UIActions =
	| DraggingStartedAction
	| DraggingEndedAction
	| DrawerOpenedAction
	| DrawerClosedAction
	| DrawerToggledAction;
